import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowLeft, Headphones, Mic, ShoppingBag, Star } from "lucide-react";

import { EpisodePlatformLinks, getPrimaryEpisodeHref } from "#/components/PlatformLinks";
import { getBookBySlug } from "#/lib/bookling";
import { gdiyEpisodeUrl } from "#/lib/gdiy";
import { SITE_NAME, absoluteUrl, bookPath, canonicalLink, jsonLdScript, seoMeta } from "#/lib/seo";

import type { BookDetailEpisode } from "#/lib/bookling-data";

export const Route = createFileRoute("/books/$slug")({
  loader: async ({ params }) => {
    const book = await getBookBySlug({ data: params.slug });
    if (!book) throw notFound();
    return book;
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: seoMeta({
          title: `Book | ${SITE_NAME}`,
          description: "A book detail page from the GDIY book mention archive.",
        }),
      };
    }

    const path = bookPath(loaderData.slug);
    const url = absoluteUrl(path);
    const description = `Episodes of GDIY where guests recommended ${loaderData.title} by ${loaderData.author}.`;

    return {
      meta: seoMeta({
        title: `${loaderData.title} by ${loaderData.author} | ${SITE_NAME}`,
        description,
        path,
        image: loaderData.imageUrl,
        type: "book",
      }),
      links: canonicalLink(path),
      scripts: [
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "Book",
          name: loaderData.title,
          author: {
            "@type": "Person",
            name: loaderData.author,
          },
          image: loaderData.imageUrl ?? undefined,
          url,
          mainEntityOfPage: url,
          subjectOf: loaderData.episodes.slice(0, 25).map((episode) => ({
            "@type": "PodcastEpisode",
            name: episode.title,
            episodeNumber: episode.number,
            description: episode.context ?? undefined,
            url: gdiyEpisodeUrl(episode.guest) ?? undefined,
            partOfSeries: {
              "@type": "PodcastSeries",
              name: "Generation Do It Yourself",
              url: "https://www.gdiy.fr/",
            },
          })),
        }),
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Books",
              item: absoluteUrl("/"),
            },
            {
              "@type": "ListItem",
              position: 2,
              name: loaderData.title,
              item: url,
            },
          ],
        }),
      ],
    };
  },
  component: BookDetailPage,
  notFoundComponent: BookNotFound,
});

function BookDetailPage() {
  const book = Route.useLoaderData();

  return (
    <main className="book-detail">
      <div className="page-wrap">
        <nav className="detail-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" className="detail-breadcrumb-link">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Bookshelf
          </Link>
          <span aria-hidden="true">/</span>
          <span className="detail-breadcrumb-current">Book detail</span>
        </nav>

        <header className="detail-intro">
          <p className="eyebrow">From the GDIY archive</p>
          <h1>This one keeps coming up.</h1>
          <p>
            A book the guests of <em>Generation Do It Yourself</em> have pressed into the
            conversation, episode after episode.
          </p>
        </header>

        <article className="detail-card">
          <div className="detail-cover">
            {book.imageUrl ? (
              <img
                src={book.imageUrl}
                alt={`${book.title} cover`}
                loading="eager"
                decoding="async"
              />
            ) : (
              <div className="detail-cover-placeholder" aria-hidden="true">
                <span>{book.title.slice(0, 1)}</span>
              </div>
            )}
          </div>
          <div className="detail-card-body">
            <h2 className="detail-title">&ldquo;{book.title}&rdquo;</h2>
            <p className="detail-author">By {book.author}</p>
            {typeof book.rating === "number" ? (
              <p className="detail-rating">
                <Star className="h-4 w-4" aria-hidden="true" />
                <strong>{book.rating.toFixed(1)}</strong>
                <span>/ 5</span>
              </p>
            ) : null}
            {book.amazonUrl ? (
              <a
                className="detail-cta"
                href={book.amazonUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                <ShoppingBag className="h-4 w-4" aria-hidden="true" />
                See on Amazon
              </a>
            ) : null}
          </div>
        </article>

        <section className="detail-episodes" aria-labelledby="recommending-heading">
          <header className="detail-episodes-header">
            <h2 id="recommending-heading">
              <Mic className="h-5 w-5" aria-hidden="true" />
              Episodes recommending this book
            </h2>
            <span className="detail-episodes-count">
              {book.episodes.length} {book.episodes.length === 1 ? "episode" : "episodes"}
            </span>
          </header>

          {book.episodes.length === 0 ? (
            <p className="library-empty">
              <Headphones className="h-4 w-4" aria-hidden="true" />
              Not yet linked to an episode in our archive.
            </p>
          ) : (
            <ul className="recommend-list">
              {book.episodes.map((ep, i) => (
                <RecommendingEpisode key={ep.id} episode={ep} highlighted={i === 0} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}

function RecommendingEpisode({
  episode,
  highlighted,
}: {
  episode: BookDetailEpisode;
  highlighted: boolean;
}) {
  const primaryHref = getPrimaryEpisodeHref(episode);

  const inner = (
    <>
      <div className="recommend-avatar" aria-hidden="true">
        {episode.guestImage ? (
          <img src={episode.guestImage} alt="" loading="lazy" />
        ) : (
          <span>{episode.guest.slice(0, 1).toUpperCase()}</span>
        )}
      </div>
      <div className="recommend-body">
        <p className="recommend-title">
          <span className="recommend-number">#{episode.number}</span>
          <span className="recommend-sep">·</span>
          <span>
            {episode.guest} {episode.company ? <em>· {episode.company}</em> : null}
          </span>
        </p>
        <p className="recommend-headline">{episode.title}</p>
        {episode.context ? <p className="recommend-context">{episode.context}</p> : null}
        {episode.timestamp ? <p className="recommend-timestamp">at {episode.timestamp}</p> : null}
      </div>
    </>
  );

  return (
    <li className={highlighted ? "recommend-row is-highlight" : "recommend-row"}>
      {primaryHref ? (
        <a
          className="recommend-row-link"
          href={primaryHref}
          target="_blank"
          rel="noreferrer noopener"
        >
          {inner}
        </a>
      ) : (
        <div className="recommend-row-inner">{inner}</div>
      )}
      <EpisodePlatformLinks episode={episode} className="recommend-platforms" />
    </li>
  );
}

function BookNotFound() {
  return (
    <main className="book-detail">
      <div className="page-wrap detail-missing">
        <p className="eyebrow">Not in our archive</p>
        <h1>We could not find that book.</h1>
        <p>It might have been renamed or removed.</p>
        <Link to="/" className="detail-cta detail-cta--ghost">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to the bookshelf
        </Link>
      </div>
    </main>
  );
}
